import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import downloadFile from 'downloadjs'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { useSegment } from '../../../hooks'
import { APIError, getCurrentLocale } from '../../../utils'
import { downloadBankLines } from '../query-api'

const BANK_LINES_FILE_NAME = 'bank-lines.csv'
const EXPORT_LIMIT_EXCEEDED_ERROR_CODE = 'BANK_LINES_CSV_EXPORT_LIMIT_EXCEEDED'

interface UseDownloadBankLinesProps {
  onError?: (error: unknown) => void
  onSuccess?: () => void
}

export const useDownloadBankLines = (props?: UseDownloadBankLinesProps) => {
  const { onError, onSuccess } = props || {}
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { track } = useSegment()

  const organizationId = organization?.id

  const handleError = useCallback(
    (error: unknown) => {
      const isExportLimitExceeded =
        error instanceof APIError && error.body?.errorCode === EXPORT_LIMIT_EXCEEDED_ERROR_CODE

      if (!isExportLimitExceeded) {
        trackError(error)
      }

      notify({
        id: NotificationKeys.BankAccountDownloadTransactionsError,
        message: isExportLimitExceeded
          ? t('bank.accounts.list.card.download_transactions.too_many_lines')
          : t('error_saving'),
        variant: 'error',
      })
      onError?.(error)
    },
    [onError, t],
  )

  const handleSuccess = useCallback(() => {
    track('XXX - Mikkel - Exports', {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      export_type: 'Bank lines',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      file_format: 'csv',
      period: 'all',
    })
    onSuccess?.()
  }, [onSuccess, track])

  const {
    mutate: download,
    isLoading: isProcessing,
    ...rest
  } = useMutation(
    async (accountId: string) => {
      if (!organizationId) {
        throw new Error('Missing organization id')
      }

      const response = await downloadBankLines({ accountId, locale: getCurrentLocale(), organizationId })
      const blob = await response.blob()

      downloadFile(blob, BANK_LINES_FILE_NAME, 'text/csv')
    },
    { onError: handleError, onSuccess: handleSuccess },
  )

  return { download, isProcessing, ...rest }
}
